/**
 * Générateur — Automatisme : placer les aiguilles (heure donnée)
 * Attendu ID : 1032 | Niveau : 6ème | Domaine : GM
 * Rubrique : AUTOMATISME (programme 2025) — « L'élève place les aiguilles
 * pour qu'une horloge indique une heure donnée » (en heures, minutes ET
 * secondes — la trotteuse est la nouveauté CM2).
 * Labels (cartes exerciseur) :
 *   - Placer les aiguilles (heure donnée)
 *
 * ── Plan de cartes (2026-08-22, cadrage prof) ──────────────────────────────
 * UN SEUL format, sur le composant dédié HorlogeAiguillesEleve (le geste
 * réciproque du gen_1031). Aiguilles INDÉPENDANTES : le savoir évalué est
 * « à 5 h 30, la petite est ENTRE le 5 et le 6 » — validation : la grande
 * (et la trotteuse) sur le bon repère de 5 min ; la petite STRICTEMENT à
 * l'intérieur du bon intervalle (pile sur le chiffre n'est juste qu'aux
 * heures piles). Règles dans questionUtils.aiguilleJuste.
 * Distribution des consignes :
 *   - ~15 % en 24 h (« Il est 21 h 55... » : conversion préalable, un seul
 *     cas de ce type, arbitrage prof) ;
 *   - ~30 % avec secondes (3 aiguilles) ;
 *   - min = 0 ~15 % (l'ancrage « pile sur le chiffre ») ;
 *   - sinon zone piège min ∈ [35 ; 55] majoritaire (la petite frôle le
 *     chiffre suivant sans l'atteindre — le pendant du piège de lecture) ;
 *   - h ∈ [1 ; 11] (midi/minuit écartés).
 */
import {
    randomChoice, randInt,
    makePlacerAiguilles,
} from '../../../utils';

const ATTENDU_ID = 1032;
const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L1 = 'Placer les aiguilles (heure donnée)';

const pad2 = (n) => String(n).padStart(2, '0');

// ─── Le format unique : placer les aiguilles ──────────────────────────────

const genPlacerAiguilles = () => {
    const h = randInt(1, 11);
    let min;
    const r = Math.random();
    if (r < 0.15) min = 0;                                     // l'ancrage « pile »
    else if (r < 0.70) min = randomChoice([35, 40, 45, 50, 55]); // la zone du piège
    else min = randomChoice([5, 10, 15, 20, 25, 30]);
    const sec = Math.random() < 0.3
        ? randomChoice([5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55])
        : null;

    // ~15 % : consigne en 24 h (conversion préalable — un seul cas de ce type)
    const en24h = sec === null && Math.random() < 0.15;
    let texte;
    if (en24h) {
        const h24 = h + 12;
        texte = `<p><b>Il est ${h24} h ${pad2(min)}. Place les aiguilles de l'horloge.</b></p>`;
    } else if (sec !== null) {
        texte = `<p><b>Place les aiguilles pour que l'horloge indique ${h} h ${pad2(min)} min ${pad2(sec)} s.</b></p>`;
    } else {
        texte = `<p><b>Place les aiguilles pour que l'horloge indique ${h} h ${pad2(min)}.</b></p>`;
    }

    const payload = makePlacerAiguilles({ h, min, sec }, texte, opts(L1));
    payload._meta.params = { h, min, sec, en24h };
    return payload;
};

// ─── Contrat (référence de revue) ─────────────────────────────────────────

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'format unique interactif : pas de distracteurs — l\'erreur vit dans le GESTE',
        'validation (questionUtils.aiguilleJuste) : grande aiguille et trotteuse sur le bon repère de 5 min ; petite aiguille strictement DANS ]h ; h+1[ (marge 2°), pile sur le chiffre seulement quand min = 0 (±6°)',
        'h ∈ [1 ; 11], minutes et secondes multiples de 5',
        'zone piège min ∈ [35 ; 55] majoritaire : poser la petite pile sur le chiffre suivant est compté faux',
        '~15 % de consignes en 24 h (conversion préalable), ~30 % avec trotteuse, ~15 % d\'heures piles',
    ],
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    placer_aiguilles: genPlacerAiguilles,
};

const FORMAT_LABELS = {
    placer_aiguilles: 'Horloge',
};

export const CATEGORIES = [
    {
        id: 'placeraiguilles',
        label: L1,
        formatsList: Object.keys(GENERATORS).map((k) => ({ key: k, label: FORMAT_LABELS[k] || k })),
    },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
